import { useEffect, useRef } from "react";

const DIGIT = /^[0-9]$/;
const isTypingElsewhere = (el, allow) => {
  if (!el || el === document.body) return false;
  if (allow && el.closest && el.closest(allow)) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
};

/**
 * NEEC POS core standard: physical keyboard + numpad drive any on-screen numeric keypad.
 * Digits (row + numpad) -> onDigit, Backspace -> onBackspace, Delete -> onClear,
 * Enter -> onEnter, Escape -> onEscape. Letters/symbols ignored. Listener only lives while `active`.
 * `ignoreWithin` (CSS selector) lets the keypad's own inputs keep normal typing.
 */
export function useKeypadKeys({ active, onDigit, onBackspace, onClear, onEnter, onEscape, ignoreWithin }) {
  const h = useRef({});
  h.current = { onDigit, onBackspace, onClear, onEnter, onEscape };
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingElsewhere(e.target, ignoreWithin)) return;
      const k = e.key;
      const f = h.current;
      // Numpad keys report Home/End/Clear when NumLock is off — still treat them as digits on a POS.
      const npad = /^Numpad([0-9])$/.exec(e.code || "");
      if (DIGIT.test(k) || npad) { e.preventDefault(); f.onDigit?.(DIGIT.test(k) ? k : npad[1]); }
      else if (k === "Backspace") { e.preventDefault(); f.onBackspace?.(); }
      else if (k === "Delete") { e.preventDefault(); f.onClear?.(); }
      else if (k === "Enter") { e.preventDefault(); f.onEnter?.(); }
      else if (k === "Escape") { f.onEscape?.(); }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [active, ignoreWithin]);
}
